<template>
  <AuthenticatedDashboardShell v-if="config.isAuthenticated" :is-embedded="isEmbedded" />
  <PublicDashboardShell v-else :is-embedded="isEmbedded" />
  <Teleport v-if="errorOpen" to="body">
    <ErrorModal
      :is-open="errorOpen"
      :title="errorTitle"
      :message="errorMessage"
      :variant="errorVariant"
      :action-label="errorActionLabel"
      @close="closeError"
      @confirm="confirmError"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, provide, watch } from "vue";

import ErrorModal from "@core/components/common/ErrorModal.vue";
import { useAppConfig } from "@core/composables/data/useAppConfig";
import { useErrorModal } from "@core/composables/state/useErrorModal";
import { useLanguage, type Language } from "@core/composables/state/useLanguage";
import { useNavigation } from "@core/composables/state/useNavigation";
import { provideRouteContext, provideRouterContext } from "@core/composables/state/useRouteContext";
import { useTheme, type ThemeContext, type ThemeMode } from "@core/composables/ui/useTheme";

import PublicDashboardShell from "./layouts/PublicDashboardShell.vue";

const AuthenticatedDashboardShell = defineAsyncComponent(() => import("./layouts/AuthenticatedDashboardShell.vue"));

type YouParkApi = {
  setEmbedded(value: boolean): void;
  setTheme(mode: ThemeMode): void;
  setLanguage(value: Language | string): void;
  getState(): {
    embedded: boolean;
    theme: ThemeMode;
    language: "nb" | "en";
  };
};

type YouParkNative = {
  onAuthCompleted(): void;
};

declare global {
  interface Window {
    YouParkApi?: YouParkApi;
    youparkAccountV2?: YouParkApi;
    YouParkNative?: YouParkNative;
  }
}

const { config } = useAppConfig();
const route = provideRouteContext();
const router = provideRouterContext();
const { activeView, routeParams, navigateTo } = useNavigation();

const theme = useTheme();
provide<ThemeContext>("theme", {
  isDark: theme.isDark,
  preference: theme.preference,
  activeMode: theme.activeMode,
  setTheme: theme.setTheme
});

onMounted(() => {
  theme.init();
});

onBeforeUnmount(() => {
  theme.cleanup();
});

const { language: currentLanguage, setLanguage: applyLanguage, toLanguageTag } = useLanguage();

const getQueryValue = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    return getQueryValue(value[0]);
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
};

const normalizeThemeMode = (value: unknown): ThemeMode | null => {
  const stringValue = getQueryValue(value);
  if (!stringValue) {
    return null;
  }
  const normalized = stringValue.trim().toLowerCase();
  if (normalized === "light" || normalized === "dark" || normalized === "auto") {
    return normalized;
  }
  return null;
};

const parseEmbeddedFlag = (value: unknown): boolean => {
  const stringValue = getQueryValue(value);
  if (stringValue === null) {
    return false;
  }
  const normalized = stringValue.trim().toLowerCase();
  if (normalized.length === 0) {
    return true;
  }
  if (normalized === "0" || normalized === "false" || normalized === "no" || normalized === "off") {
    return false;
  }
  if (normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on") {
    return true;
  }
  return true;
};

const isEmbedded = computed(() => parseEmbeddedFlag(route.query.embedded) || config.isNativeClient);

const applyThemeFromQuery = (value: unknown) => {
  const mode = normalizeThemeMode(value);
  if (!mode) {
    return;
  }
  theme.setTheme(mode);
};

const applyLanguageFromQuery = (value: unknown) => {
  const stringValue = getQueryValue(value);
  if (!stringValue) {
    return;
  }
  applyLanguage(stringValue);
};

const updateEmbeddingQuery = (updates: Partial<Record<"embedded" | "theme" | "lang", string | null>>) => {
  const currentRoute = router.currentRoute.value;
  const nextQuery: Record<string, string | undefined> = {};
  Object.entries(currentRoute.query).forEach(([key, value]) => {
    const normalizedValue = getQueryValue(value);
    if (normalizedValue !== null) {
      nextQuery[key] = normalizedValue;
    }
  });
  let changed = false;

  (Object.entries(updates) as [keyof typeof updates, string | null][]).forEach(([key, value]) => {
    if (value === null) {
      if (key in nextQuery) {
        delete nextQuery[key];
        changed = true;
      }
      return;
    }
    if (nextQuery[key] !== value) {
      nextQuery[key] = value;
      changed = true;
    }
  });

  if (!changed) {
    return;
  }

  void navigateTo(activeView.value, routeParams.value, { query: nextQuery, replace: true });
};

watch(
  () => route.query.theme,
  value => {
    applyThemeFromQuery(value);
  },
  { immediate: true }
);

watch(
  () => route.query.lang,
  value => {
    applyLanguageFromQuery(value);
  },
  { immediate: true }
);

const controller: YouParkApi = {
  setEmbedded(value: boolean) {
    if (value) {
      updateEmbeddingQuery({
        embedded: "1",
        theme: theme.preference.value,
        lang: toLanguageTag(currentLanguage.value)
      });
      return;
    }
    updateEmbeddingQuery({ embedded: null, theme: null, lang: null });
  },
  setTheme(mode: ThemeMode) {
    const normalized = normalizeThemeMode(mode);
    if (!normalized) {
      return;
    }
    theme.setTheme(normalized);
    updateEmbeddingQuery({ theme: normalized });
  },
  setLanguage(value: Language | string) {
    applyLanguage(value);
    updateEmbeddingQuery({ lang: toLanguageTag(currentLanguage.value) });
  },
  getState() {
    return {
      embedded: isEmbedded.value,
      theme: theme.preference.value,
      language: toLanguageTag(currentLanguage.value)
    };
  }
};

watch(theme.preference, mode => {
  if (!isEmbedded.value && route.query.theme === undefined) {
    return;
  }
  updateEmbeddingQuery({ theme: mode });
});

watch(currentLanguage, lang => {
  if (!isEmbedded.value && route.query.lang === undefined) {
    return;
  }
  updateEmbeddingQuery({ lang: toLanguageTag(lang) });
});

const handleExternalLinks = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  const link = target.closest("a");

  if (!link || !link.href) {
    return;
  }

  try {
    const linkUrl = new URL(link.href, window.location.href);
    const isExternal = linkUrl.hostname !== window.location.hostname;

    if (isExternal) {
      const isNewTab = link.target === "_blank" || e.ctrlKey || e.metaKey || e.button === 1;
      console.log("[Embedded] Blocking external navigation:", {
        href: link.href,
        hostname: linkUrl.hostname,
        currentHostname: window.location.hostname,
        target: link.target,
        isNewTab,
        modifierKeys: { ctrl: e.ctrlKey, meta: e.metaKey, button: e.button }
      });
      e.preventDefault();
      e.stopPropagation();
    }
  } catch (error) {
    console.warn("[Embedded] Failed to parse link URL:", link.href, error);
  }
};

watch(
  isEmbedded,
  value => {
    if (typeof window === "undefined") {
      return;
    }
    if (value) {
      console.log("[Embedded] Enabling embedded mode - blocking external navigation");
      document.addEventListener("click", handleExternalLinks, true);
      window.YouParkApi = controller;
      window.youparkAccountV2 = controller;
      return;
    }
    console.log("[Embedded] Disabling embedded mode - allowing external navigation");
    document.removeEventListener("click", handleExternalLinks, true);
    if (window.YouParkApi === controller) {
      delete window.YouParkApi;
    }
    if (window.youparkAccountV2 === controller) {
      delete window.youparkAccountV2;
    }
  },
  { immediate: true }
);

if (config.isAuthenticated) {
  window.YouParkNative?.onAuthCompleted();
}

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    document.removeEventListener("click", handleExternalLinks, true);
    if (window.YouParkApi === controller) {
      delete window.YouParkApi;
    }
    if (window.youparkAccountV2 === controller) {
      delete window.youparkAccountV2;
    }
  }
});

const {
  isOpen: errorOpen,
  title: errorTitle,
  message: errorMessage,
  variant: errorVariant,
  actionLabel: errorActionLabel,
  close: closeError,
  confirm: confirmError
} = useErrorModal();
</script>
